import { BrandFixedFeeConfig, FixedFee, FixedFeeLineCount, FixedFeeRow } from '../../../types/FixedFee'
import { buildSiteLabelGroups, groupBy } from './overviewUtils'

const SCOPE_BADGE_COLOR: Record<string, string> = {
    'Global': 'tw-bg-gray-500',
    'Site': 'tw-bg-blue-500',
    'Marque': 'tw-bg-purple-500',
    'Site + Marque': 'tw-bg-orange-500',
}

// Même style que getDepartmentBadge (WorkingHoursTable.tsx) : classes Tailwind directement,
// couleur selon le Périmètre.
export function scopeBadgeClass(label: string): string {
    return `tw-px-3 tw-py-1 tw-rounded tw-text-white tw-font-semibold ${SCOPE_BADGE_COLOR[label] ?? 'tw-bg-gray-500'}`
}

export function buildFixedFeeQuery(selectedSiteId: number | null, selectedBrand: string | null): string {
    const params = new URLSearchParams()
    if (selectedSiteId != null) {
        params.set('dealership_id', String(selectedSiteId))
    }
    if (selectedBrand) {
        params.set('brand', selectedBrand)
    }
    const query = params.toString()
    return query ? `?${query}` : ''
}

// Une ligne renvoyée par l'API est "à moi" si ses dimensions correspondent exactement au scope
// affiché — sinon c'est un défaut emprunté ailleurs (catalogue global, ou config du site/de la
// marque dans l'aperçu fusionné d'une combinaison pas encore enregistrée).
export function isFixedFeeMaterializedForMe(row: FixedFee, selectedSiteId: number | null, selectedBrand: string | null): boolean {
    return row.dealership_id === selectedSiteId && row.brand === selectedBrand
}

// Verrouillée (actif/inactif seulement) si c'est un miroir, ou si la ligne est juste empruntée
// en aperçu (pas encore matérialisée pour ce scope exact).
export function isFixedFeeLocked(row: FixedFeeRow, selectedSiteId: number | null, selectedBrand: string | null): boolean {
    return row.source_id != null || !isFixedFeeMaterializedForMe(row, selectedSiteId, selectedBrand)
}

// Badge affiché dans la colonne Périmètre : pour un miroir, l'origine de sa racine (déjà résolue
// côté API) ; pour une racine empruntée, ses propres dimensions ; pour une racine matérialisée
// ici, ses propres dimensions aussi (peut donner "Site + Marque").
export function fixedFeeScopeLabel(row: FixedFeeRow, selectedSiteId: number | null, selectedBrand: string | null): string {
    if (row.source_id != null) {
        return row.origin === 'site' ? 'Site' : row.origin === 'brand' ? 'Marque' : 'Global'
    }
    if (!isFixedFeeMaterializedForMe(row, selectedSiteId, selectedBrand)) {
        if (row.dealership_id != null) return 'Site'
        if (row.brand != null) return 'Marque'
        return 'Global'
    }
    if (row.dealership_id != null && row.brand != null) return 'Site + Marque'
    if (row.dealership_id != null) return 'Site'
    if (row.brand != null) return 'Marque'
    return 'Global'
}

// Regroupe les lignes ayant un site (exclut les lignes génériques marque-seule/globales).
export function groupByDealershipId(rows: FixedFee[]): Map<number, FixedFee[]> {
    return groupBy(rows.filter((row): row is FixedFee & { dealership_id: number } => row.dealership_id !== null), row => row.dealership_id)
}

/** Signature d'une config (pour fusionner les sites strictement identiques dans une même ligne). */
function signatureOf(rows: FixedFee[]): string {
    return rows
        .map(row => `${row.fee_type}:${row.amount}:${row.is_active}`)
        .sort()
        .join(',')
}

/** Nombre de lignes actives sur le total des lignes existantes pour l'entité (miroirs compris). */
export function countActiveTotal(rows: FixedFee[]): FixedFeeLineCount {
    return { active: rows.filter(row => row.is_active).length, total: rows.length }
}

// Pour l'aperçu : une ligne par marque, avec ses sites regroupés quand ils ont exactement la
// même config (même signature), + une ligne générique "-" si la marque a des frais sans site.
export function buildBrandConfigs(
    brandScopedData: FixedFee[],
    siteNameById: Map<number, string>,
): BrandFixedFeeConfig[] {
    const byBrand = groupBy(brandScopedData, row => row.brand as string)

    return Array.from(byBrand.entries()).flatMap(([ brand, rows ]) => {
        const genericRows = rows.filter(row => row.dealership_id === null)
        const bySite = groupByDealershipId(rows)

        const siteSummaries = Array.from(bySite.entries()).map(([ dealershipId, siteRows ]) => ({
            dealershipId,
            signature: signatureOf(siteRows),
            value: countActiveTotal(siteRows),
        }))

        const brandConfigs: BrandFixedFeeConfig[] = buildSiteLabelGroups(siteSummaries, siteNameById)
            .map(({ siteLabel, dealershipIds, value }) => ({ brand, siteLabel, dealershipIds, linesConfigured: value }))

        if (genericRows.length > 0) {
            brandConfigs.push({ brand, siteLabel: '-', dealershipIds: [], linesConfigured: countActiveTotal(genericRows) })
        }

        return brandConfigs
    })
}
